"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.useSubmitPaymentRequest = exports.InvalidUpdatePaymentRequest = void 0;
const tslib_1 = require("tslib");
/*
Copyright (C) 2009 - 2021 Broadleaf Commerce.

Licensed under the Broadleaf End User License Agreement (EULA),
Version 1.1 (the “Commercial License” located at
http://license.broadleafcommerce.org/commercial_license-1.1.txt).

Alternatively, the Commercial License may be replaced with a mutually
agreed upon license (the “Custom License”) between you and
Broadleaf Commerce. You may not use this file except in compliance
with the applicable license.
*/
const react_1 = require("react");
const commerce_cart_1 = require("@broadleaf/commerce-cart");
const use_create_payment_1 = require("./use-create-payment");
const use_update_payment_1 = require("./use-update-payment");
class InvalidUpdatePaymentRequest extends Error {
    constructor(message) {
        super(message);
        this.name = 'InvalidUpdatePaymentRequest';
    }
}
exports.InvalidUpdatePaymentRequest = InvalidUpdatePaymentRequest;
/**
 * This hook will handle submitting payment info including whether to update the existing payment or
 * add a new one.
 */
const useSubmitPaymentRequest = (props) => {
    const { authState, ownerId, ownerType, payments, multiplePaymentsAllowed = false, rejectOnError = false } = props;
    const { createPayment } = (0, use_create_payment_1.useCreatePayment)(Object.assign(Object.assign({}, props), { rejectOnError: true }));
    const { updatePayment } = (0, use_update_payment_1.useUpdatePayment)(Object.assign(Object.assign({}, props), { rejectOnError: true }));
    const [error, setError] = (0, react_1.useState)();
    const [submitting, setSubmitting] = (0, react_1.useState)(false);
    const handleSubmitPaymentInfo = (0, react_1.useCallback)((paymentRequest, paymentToUpdateId) => (0, tslib_1.__awaiter)(void 0, void 0, void 0, function* () {
        var _a;
        setSubmitting(true);
        setError(undefined);
        try {
            const owningUserType = authState.accountId != undefined
                ? commerce_cart_1.DefaultPaymentOwningUserType.BLC_ACCOUNT
                : commerce_cart_1.DefaultPaymentOwningUserType.BLC_CUSTOMER;
            if (!multiplePaymentsAllowed && ((_a = payments === null || payments === void 0 ? void 0 : payments.content) === null || _a === void 0 ? void 0 : _a.length)) {
                return yield handleUpdateOnlyPayment(payments, paymentRequest, owningUserType, updatePayment);
            }
            if (paymentToUpdateId) {
                return yield handleUpdateExistingPayment(payments, paymentRequest, paymentToUpdateId, owningUserType, updatePayment);
            }
            const createPaymentRequest = Object.assign({ ownerId, ownerType: ownerType || commerce_cart_1.DefaultPaymentOwnerType.BLC_CART, owningUserType: owningUserType }, paymentRequest);
            return yield createPayment(createPaymentRequest);
        }
        catch (err) {
            setError(err);
            if (rejectOnError) {
                throw err;
            }
            return;
        }
        finally {
            setSubmitting(false);
        }
    }), [
        authState.accountId,
        ownerId,
        ownerType,
        multiplePaymentsAllowed,
        payments,
        createPayment,
        updatePayment,
        rejectOnError
    ]);
    return { error, submitting, handleSubmitPaymentInfo };
};
exports.useSubmitPaymentRequest = useSubmitPaymentRequest;
function handleUpdateOnlyPayment(payments, paymentRequest, owningUserType, updatePayment) {
    return (0, tslib_1.__awaiter)(this, void 0, void 0, function* () {
        const { paymentId, version } = payments.content[0];
        const updatePaymentRequest = Object.assign({ paymentId, owningUserType: owningUserType }, paymentRequest);
        return yield updatePayment(paymentId, version, updatePaymentRequest);
    });
}
function handleUpdateExistingPayment(payments, paymentRequest, paymentToUpdateId, owningUserType, updatePayment) {
    var _a;
    return (0, tslib_1.__awaiter)(this, void 0, void 0, function* () {
        const payment = (_a = payments === null || payments === void 0 ? void 0 : payments.content) === null || _a === void 0 ? void 0 : _a.filter(({ paymentId }) => paymentId === paymentToUpdateId)[0];
        if ((payment === null || payment === void 0 ? void 0 : payment.version) === null || (payment === null || payment === void 0 ? void 0 : payment.version) === undefined) {
            throw new InvalidUpdatePaymentRequest(`No payment with ID '${paymentToUpdateId}' to update.`);
        }
        const updatePaymentRequest = Object.assign({ paymentId: paymentToUpdateId, owningUserType: owningUserType }, paymentRequest);
        return yield updatePayment(paymentToUpdateId, payment.version, updatePaymentRequest);
    });
}
//# sourceMappingURL=use-submit-payment-request.js.map